import React, { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CreditApplicationCategory, CreditApplicationSortField, SortDirection } from '_gql'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import BasicButton from '@divvy-web/skylab.basicbutton'
import { DataGrid, FooterToolbar, LoadMore, Table, ZeroState } from '@divvy-web/skylab.datagridnext'
import { getCoreRowModel, SortingState, TableState, useReactTable } from '@divvy-web/skylab.tanstack-react-table'

import { PATHNAME_CONSTS } from '@sputnik/resources/constants'

import { getColumnConfiguration } from './columns'
import DashboardDetailsDrawer from './DashboardDetailsDrawer'
import DataGridHeader from './DataGridHeader'
import {
  GetCreditApplications_creditApplicationsPaginated_CreditApplicationPaginatedConnection_edges_CreditApplicationPaginatedEdge,
  useGetCreditApplications,
} from './GetCreditApplications.gql'
import styles from './styles.module.scss'

type CreditApplication =
  GetCreditApplications_creditApplicationsPaginated_CreditApplicationPaginatedConnection_edges_CreditApplicationPaginatedEdge

const getSortField = (value: string) => {
  switch (value) {
    case 'business-name':
      return CreditApplicationSortField.LEGAL_BUSINESS_NAME
    case 'status':
      return CreditApplicationSortField.STATUS_WEIGHT
    case 'line-of-credit':
      return CreditApplicationSortField.FUNDABLE_LIMIT
    case 'expires-at':
      return CreditApplicationSortField.EXPIRES_AT
    case 'started-at':
    default:
      return CreditApplicationSortField.STARTED_AT
  }
}

const getColumnFilterCreditApplicationCategory = (value: unknown) => {
  switch (value) {
    case CreditApplicationCategory.ACTION_NEEDED:
      return CreditApplicationCategory.ACTION_NEEDED
    case CreditApplicationCategory.COMPLETED:
      return CreditApplicationCategory.COMPLETED
    case CreditApplicationCategory.IN_REVIEW:
      return CreditApplicationCategory.IN_REVIEW
    case CreditApplicationCategory.ALL:
    default:
      return CreditApplicationCategory.ALL
  }
}

const pageSize = 20

const CreditApplicationsDataGrid = () => {
  const [getClassName] = useNameFormatter('CreditApplicationsDataGrid')
  const navigate = useNavigate()

  const onStartNewApplication = () => {
    navigate(PATHNAME_CONSTS.GET_TO_KNOW_YOU)
  }

  const [name, setName] = useState('')
  const [category, setCategory] = useState<CreditApplicationCategory | null>(null)
  const [sorting, setSorting] = useState<SortingState>([])
  const [orderBy, setOrderBy] = useState<CreditApplicationSortField[]>([])
  const [orderDirections, setOrderDirections] = useState<SortDirection[]>([])

  const { data, loading, fetchMore, refetch } = useGetCreditApplications({
    variables: {
      first: pageSize,
    },
  })

  const { edges, pageInfo, totalCount } = data?.creditApplicationsPaginated ?? {}
  const { hasNextPage, endCursor } = pageInfo ?? {}
  const [rowClickedApplicationDetails, setRowClickedApplicationDetails] = useState<string | null>(null)

  const columns = getColumnConfiguration({
    onViewDetailsClick: (newSalesforceCreditId: string) => setRowClickedApplicationDetails(newSalesforceCreditId),
  })

  const applications = useMemo(() => edges?.filter((edge): edge is CreditApplication => edge !== null) ?? [], [edges])

  const state: Partial<TableState> = useMemo(
    () => ({ globalFilter: name, columnFilters: category ? [{ id: 'status', value: category }] : [], sorting }),
    [category, name, sorting],
  )

  const table = useReactTable({
    data: applications,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualFiltering: true,
    manualSorting: true,
    state,
    onGlobalFilterChange: (value) => {
      // calling refetch resets the pagination / search
      setName(value)
      refetch({
        first: pageSize,
        name: value,
        category,
        orderBy,
        orderDirections,
      })
    },
    onSortingChange: (updater) => {
      const sortingState = typeof updater === 'function' ? updater(state.sorting ?? []) : updater

      setSorting(sortingState)
      const orderBy: CreditApplicationSortField[] = []
      const orderDirections: SortDirection[] = []
      sortingState.forEach(({ id, desc }) => {
        orderBy.push(getSortField(id))
        orderDirections.push(desc ? SortDirection.DESC : SortDirection.ASC)
      })

      setOrderBy(orderBy)
      setOrderDirections(orderDirections)

      refetch({
        first: pageSize,
        name,
        category,
        orderBy,
        orderDirections,
      })
    },
    onColumnFiltersChange: (updater) => {
      const filters = typeof updater === 'function' ? updater(state.columnFilters ?? []) : updater
      if (!filters?.[0]) {
        return
      }
      const [{ value }] = filters
      const newCategory = getColumnFilterCreditApplicationCategory(value)
      setCategory(newCategory)
      refetch({
        first: pageSize,
        name,
        category: newCategory,
        orderBy,
        orderDirections,
      })
    },
  })

  const handleLoadMore = useCallback(() => {
    if (hasNextPage) {
      fetchMore({
        variables: {
          after: endCursor,
          first: pageSize,
          name,
          category,
          orderBy,
          orderDirections,
        },
      })
    }
  }, [category, endCursor, fetchMore, hasNextPage, name, orderBy, orderDirections])

  return (
    <div className={`${styles[getClassName('')]} fs-unmask`}>
      <div className={styles[getClassName('title')]}>
        <FormattedMessage
          defaultMessage='Manage your applications'
          id='sputnik.CreditApplicationsDataGrid__Lecg3+'
        />
        <BasicButton onClick={onStartNewApplication}>
          <FormattedMessage
            defaultMessage='New application'
            id='sputnik.CreditApplicationsDataGrid__HOulGI'
          />
        </BasicButton>
      </div>
      <DataGrid.Root
        initialIsSearching
        initialIsShowingFilters
        hasMore={hasNextPage}
        isLoading={loading}
        table={table}
        onLoadMore={handleLoadMore}
      >
        <DataGridHeader />
        <DataGrid.Body>
          <Table<CreditApplication>
            enableInfiniteScroll
            onRowClick={(row) => {
              if (row?.original?.node?.salesforceCreditId) {
                setRowClickedApplicationDetails(row.original.node.salesforceCreditId)
              }
            }}
          />
          <DataGrid.ZeroStateInitial>
            <ZeroState.Root>
              <ZeroState.Title>
                <FormattedMessage
                  defaultMessage='You have no credit applications'
                  id='sputnik.CreditApplicationsDataGrid__GGpz3M'
                />
              </ZeroState.Title>
              <ZeroState.Content>
                <BasicButton onClick={onStartNewApplication}>
                  <FormattedMessage
                    defaultMessage='Start new application'
                    id='sputnik.CreditApplicationsDataGrid__Whk28C'
                  />
                </BasicButton>
              </ZeroState.Content>
            </ZeroState.Root>
          </DataGrid.ZeroStateInitial>
          <DataGrid.ZeroStateNoResults>
            <ZeroState.Root>
              <ZeroState.Title>
                <FormattedMessage
                  defaultMessage='No credit applications found matching your search'
                  id='sputnik.CreditApplicationsDataGrid__+FVNHE'
                />
              </ZeroState.Title>
              <ZeroState.Description>
                <FormattedMessage
                  defaultMessage='Try adjusting your search. Make sure the term you are searching for matches the legal business name on the application.'
                  id='sputnik.CreditApplicationsDataGrid__c4tKNB'
                />
              </ZeroState.Description>
            </ZeroState.Root>
          </DataGrid.ZeroStateNoResults>
        </DataGrid.Body>
        <DataGrid.Footer>
          <FooterToolbar.Root>
            {({ rowCountEnd }) => {
              return (
                <>
                  <FooterToolbar.ItemIndicator>
                    <FormattedMessage
                      defaultMessage='<pageSizeSelector>{rowCountEnd}</pageSizeSelector> of {rowCount, plural, one {{rowCount} Application} other {{rowCount} Applications}}'
                      id='sputnik.CreditApplicationsDataGrid__8NsOdP'
                      values={{
                        pageSizeSelector: (chunk) => (
                          <FooterToolbar.PageSizeSelector>{chunk}</FooterToolbar.PageSizeSelector>
                        ),
                        rowCountEnd,
                        rowCount: totalCount,
                      }}
                    />
                  </FooterToolbar.ItemIndicator>
                  <DataGrid.FlexSpacer />
                  <LoadMore
                    buttonLabel={
                      <FormattedMessage
                        defaultMessage='Load More'
                        id='sputnik.CreditApplicationsDataGrid__xMnH8F'
                      />
                    }
                    loadingLabel={
                      <FormattedMessage
                        defaultMessage='Loading...'
                        id='sputnik.CreditApplicationsDataGrid__gjBiyj'
                      />
                    }
                    noMoreLabel={
                      <FormattedMessage
                        defaultMessage='All applications loaded'
                        id='sputnik.CreditApplicationsDataGrid__ND3S0i'
                      />
                    }
                  />
                  <FooterToolbar.ScrollControls />
                </>
              )
            }}
          </FooterToolbar.Root>
        </DataGrid.Footer>
      </DataGrid.Root>
      <DashboardDetailsDrawer
        isShowing={rowClickedApplicationDetails !== null}
        salesforceCreditId={rowClickedApplicationDetails ?? ''}
        onClose={() => setRowClickedApplicationDetails(null)}
      />
    </div>
  )
}

export default CreditApplicationsDataGrid
